import { isPlatformBrowser, isPlatformServer, NgClass } from '@angular/common';
import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import {
    ChangeDetectionStrategy,
    Component,
    DOCUMENT,
    ElementRef,
    HostBinding,
    HostListener,
    inject,
    Input,
    PLATFORM_ID,
    Renderer2,
    signal,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import classNames from 'classnames';
import { asyncScheduler, fromEvent, Subject } from 'rxjs';
import { takeUntil, throttleTime } from 'rxjs/operators';

import { ButtonV3Component } from 'components/atoms/button-v3/button-v3.component';
import type { IButtonStyleProperties } from 'components/atoms/button-v3/entities/types';
import { AbstractWebsiteComponent } from 'components/molecules/abstract-website-component/abstract-website-component';
import { HeaderV2DesktopMenuComponent } from 'components/organisms/header-v2/partials/header-v2-desktop-menu/header-v2-desktop-menu.component';
import { HeaderV2LogoComponent } from 'components/organisms/header-v2/partials/header-v2-logo/header-v2-logo.component';
import { HeaderV2MobileMenuComponent } from 'components/organisms/header-v2/partials/header-v2-mobile-menu/header-v2-mobile-menu.component';
import { LanguageSelectorButtonComponent } from 'components/organisms/header-v2/partials/language-selector-button/language-selector-button.component';
import { HeaderV2Service } from 'components/organisms/header-v2/service/header-v2.service';
import { HeaderViewService } from 'components/organisms/header-v2/service/header-view.service';
import { LinkTargetDetectorDirective } from 'src/directives/link-target-detector/link-target-detector.directive';
import type { ILocale } from 'src/providers/l10n/types';
import { ButtonV3Service } from 'src/services/button/button-v3.service';
import { CallToActionService } from 'src/services/call-to-action/call-to-action.service';
import { ThemeService } from 'src/services/theme/theme.service';
import type { ILocaleWithLink } from 'src/services/url-localise/entities';
import { INNER_WIDTH_DESKTOP_SMALL, INNER_WIDTH_TABLET } from 'src/shared/constants/breakpoints/breakpoints';
import { getDefaultTheme } from 'src/shared/helpers/get-default-theme/get-default-theme';
import type { TFullColorScheme, THeaderLayoutTheme } from 'src/shared/types/color-schemes-and-themes';
import { EProductType } from 'src/shared/types/product-type';
import { PRODUCT_TYPE } from 'src/tokens/product-type/product-type';

import type { ISecondaryNavigation, TPrimaryNavigationItem } from './entities/types';

@Component({
    selector: 'header[website-header-v2]',
    templateUrl: './header-v2.component.html',
    styleUrls: ['./header-v2.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    exportAs: 'websiteHeaderV2',
    imports: [
        NgClass,
        HeaderV2LogoComponent,
        HeaderV2DesktopMenuComponent,
        ButtonV3Component,
        HeaderV2MobileMenuComponent,
        LinkTargetDetectorDirective,
        LanguageSelectorButtonComponent,
    ],
})
export class HeaderV2Component extends AbstractWebsiteComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly document = inject<Document>(DOCUMENT);
    private readonly platformId = inject(PLATFORM_ID);
    readonly renderer = inject(Renderer2);
    private readonly vcr = inject(ViewContainerRef);
    protected readonly headerService = inject(HeaderV2Service);
    readonly elementRef = inject(ElementRef);
    readonly headerViewService = inject(HeaderViewService);
    readonly callToActionService = inject(CallToActionService);
    private readonly themeService = inject(ThemeService);
    readonly buttonV3Service = inject(ButtonV3Service);
    private readonly productType = inject(PRODUCT_TYPE);

    public isMobilePopupOpened = false;
    private mobilePopup: HTMLElement | null = null;

    public isRendered = signal(false);
    private readonly onDestroy$$ = new Subject();
    private readonly mobilePopupClosed$$ = new Subject<void>();
    private readonly HEADER_POPUP_ACTIVATION_CLASS = 'page--nav-opened';
    private readonly HEADER_OVERLAY = 'header__overlay';
    private readonly HEADER_TOP_BANNER_ID = 'layout__top-banner';
    private readonly HEADER_MOBILE_POPUP_CLASS = '.header__popup';
    private readonly HEADER_MOBILE_MENU_CLASS = '.header-menu-mobile';
    private readonly HEADER_HEIGHT_MOBILE = 52;
    private readonly MOBILE_POPUP_RESIZE_THROTTLE_MS = 250;
    private readonly headerBtnDefaultColorScheme = 'white';

    public readonly COMPONENT_NAME = 'website-header-v2';
    public readonly SHADOW_INTERACTIVE_CLASS = 'js-header__shadow';

    public buttonStyleProperties: IButtonStyleProperties;
    public readonly isKlaxoon = this.productType === EProductType.Klaxoon;

    @Input() public primaryNavigation: TPrimaryNavigationItem[];
    @Input() public secondaryNavigation: ISecondaryNavigation;
    @Input() public languages: ILocaleWithLink[];
    @Input() public currentLocale: ILocale;
    @Input() public headerTheme: TFullColorScheme | THeaderLayoutTheme;

    @HostListener('document:keydown.escape')
    onKeydownHandler() {
        this.isMobilePopupOpened && this.isPopupShow() && this.toggleMobileNav();
    }

    @HostListener('click', ['$event'])
    clicked(event: Event) {
        const className = (event.target as HTMLElement)?.className;
        className === this.HEADER_OVERLAY && this.isPopupShow() && this.toggleMobileNav();
    }

    private readonly updateMobilePopupLayout = (): void => {
        const overlayTop = this.getOverlayTopOffset();
        const mobilePopupHeight = this.getMobilePopupTargetHeight();

        const overlay = this.document.getElementById(this.HEADER_OVERLAY);
        overlay.style.top = overlayTop;
        this.mobilePopup.style.height = mobilePopupHeight + 'px';
    };

    public toggleMobileNav(): void {
        if (!this.mobilePopup) {
            this.mobilePopup = this.vcr.element.nativeElement.querySelector(this.HEADER_MOBILE_POPUP_CLASS);
        }

        if (!this.isMobilePopupOpened) {
            this.renderer.addClass(this.document.body, this.HEADER_POPUP_ACTIVATION_CLASS);

            if (isPlatformBrowser(this.platformId)) {
                this.updateMobilePopupLayout();
                this.subscribeToMobilePopupResize();
            }
        } else {
            this.renderer.removeClass(this.document.body, this.HEADER_POPUP_ACTIVATION_CLASS);

            if (isPlatformBrowser(this.platformId)) {
                this.mobilePopupClosed$$.next();
            }
        }
        this.isMobilePopupOpened = !this.isMobilePopupOpened;
        this.headerService.setMobileMenuState(this.isMobilePopupOpened);
    }

    private subscribeToMobilePopupResize(): void {
        fromEvent(window, 'resize', { passive: true })
            .pipe(
                throttleTime(this.MOBILE_POPUP_RESIZE_THROTTLE_MS, asyncScheduler, {
                    leading: true,
                    trailing: true,
                }),
                takeUntil(this.mobilePopupClosed$$),
                takeUntil(this.onDestroy$$)
            )
            .subscribe(this.updateMobilePopupLayout);
    }

    @HostBinding('class')
    public get classes(): string {
        let headerColorScheme: TFullColorScheme;

        switch (this.headerTheme) {
            case 'light':
                headerColorScheme = 'white';
                break;
            case 'dark':
                headerColorScheme = 'midnight';
                break;
            default:
                headerColorScheme = this.headerTheme;
        }

        return classNames('header', this.themeService.getClassesString(headerColorScheme));
    }

    public get height(): number {
        if (isPlatformServer(this.platformId)) {
            return 0;
        }

        return this.elementRef.nativeElement.getBoundingClientRect().height;
    }

    public ngOnInit(): void {
        this.buttonStyleProperties = {
            ...this.buttonV3Service.getStylesAccordingToPriority('primary', this.headerBtnDefaultColorScheme),
            size: 'extra-small',
        };

        if (this.isKlaxoon) {
            this.headerService.setKlaxoonLocale(this.currentLocale, this.languages);
        }
    }

    public ngAfterViewInit(): void {
        this.isRendered.set(true); //https://www.wrike.com/open.htm?id=1074358746
    }

    public ngOnDestroy(): void {
        if (isPlatformBrowser(this.platformId)) {
            this.document.defaultView?.removeEventListener('resize', this.updateMobilePopupLayout);
        }
        this.mobilePopupClosed$$.next();
        this.mobilePopupClosed$$.complete();

        this.onDestroy$$.next(null);
        this.onDestroy$$.complete();
    }

    public isDarkTheme = (colorScheme): boolean => {
        return getDefaultTheme(colorScheme as TFullColorScheme) === 'dark' || colorScheme === 'dark';
    };

    public get layoutTheme(): THeaderLayoutTheme {
        return this.isDarkTheme(this.headerTheme) ? 'dark' : 'light';
    }

    private setMaxHeightToPopup = () => {
        //height of this elements can change dynamically (resizing and closing top-banner)
        const topBanner = this.document.getElementById(this.HEADER_TOP_BANNER_ID);
        const windowHeight = this.document.documentElement.clientHeight;

        const visibleAreaForMobileMenu = windowHeight - topBanner.offsetHeight - this.HEADER_HEIGHT_MOBILE;

        this.mobilePopup.style.maxHeight = visibleAreaForMobileMenu + 'px';
    };

    private getMobilePopupTargetHeight(): number {
        return this.isTablet() ? this.getTabletPopupHeight() : this.getMobilePopupHeight();
    }

    private getMobilePopupHeight(): number {
        const topBanner = this.document.getElementById(this.HEADER_TOP_BANNER_ID);
        const bannerVisibleHeight = topBanner ? Math.max(0, topBanner.getBoundingClientRect().bottom) : 0;

        return Math.max(
            0,
            this.document.documentElement.clientHeight - bannerVisibleHeight - this.HEADER_HEIGHT_MOBILE
        );
    }

    private getTabletPopupHeight(): number {
        return this.getViewportHeight();
    }

    private getViewportHeight(): number {
        return this.document.defaultView?.innerHeight || this.document.documentElement.clientHeight;
    }

    public onStickyChange(isStuck: boolean): void {
        if (this.headerService.state.disableStickiness) {
            if (this.headerService.state.isSticky) {
                this.headerService.setHeaderStickyState(false);
            }
            return;
        }

        if (this.headerService.state.isSticky !== isStuck) {
            this.headerService.setHeaderStickyState(isStuck);
        }
    }

    public onShadowChange(shadowEnabled: boolean) {
        if (this.headerService.state.shadowEnabled !== shadowEnabled) {
            this.headerService.setHeaderShadowState(shadowEnabled);
        }
    }

    private isPopupShow = (): boolean => {
        if (isPlatformBrowser(this.platformId)) {
            return this.document.documentElement.clientWidth < INNER_WIDTH_DESKTOP_SMALL;
        } else {
            return false;
        }
    };

    private isTablet = (): boolean => {
        if (isPlatformBrowser(this.platformId)) {
            return this.document.documentElement.clientWidth >= INNER_WIDTH_TABLET;
        } else {
            return false;
        }
    };

    private getOverlayTopOffset(): string {
        const top_banner = this.document.getElementById(this.HEADER_TOP_BANNER_ID);
        return top_banner && !this.isTablet() ? top_banner.offsetHeight + 'px' : '0';
    }

    public onOpenPopup() {
        if (this.isMobilePopupOpened) {
            this.toggleMobileNav();
        }
        const { trialForm, popupTitle, popupSubtitle } = this.secondaryNavigation;
        this.callToActionService.onOpenPopup(
            'trialPopup',
            {
                ...trialForm,
                popupTitle,
                popupSubtitle,
            },
            null,
            this.headerBtnDefaultColorScheme
        );
    }
}
